import { createContext, useContext, useState, useEffect } from 'react';
import { userAPI, getTokenUserData } from 'api/users';
import { getAccessToken, refreshAccessToken, setAccessToken, setRefreshToken, clearTokens } from 'api/authFetch';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is already authenticated on mount
  useEffect(() => {
    const bootstrap = async () => {
      // 1) If we already have an access token, use it.
      let token = getAccessToken();

      // 2) Otherwise try refresh (works only if backend cookie is sent).
      if (!token) {
        token = await refreshAccessToken();
      }

      if (token) {
        setIsAuthenticated(true);
        const tokenData = getTokenUserData();
        if (tokenData) setUser(tokenData);
        await fetchCurrentUser();
        return;
      }

      setLoading(false);
    };

    bootstrap();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const userData = await userAPI.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Error fetching current user:', error);
      // Only clear auth if backend explicitly denies the token.
      const msg = String(error?.message || '');
      if (msg.includes('(401)') || msg.includes('(403)')) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;

    let canceled = false;
    let timeoutId = null;

    const schedule = () => {
      const token = getAccessToken();
      if (!token) return;

      // Decode JWT exp to refresh ~60s before expiry. If decode fails, skip scheduling.
      try {
        const [, payloadB64] = token.split('.');
        if (!payloadB64) return;
        const payloadJson = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
        const expSec = payloadJson?.exp;
        if (!expSec) return;
        const refreshInMs = Math.max(5000, expSec * 1000 - Date.now() - 60000);

        timeoutId = setTimeout(async () => {
          const newToken = await refreshAccessToken();
          if (canceled) return;
          if (newToken) {
            // Re-schedule based on new token
            schedule();
          } else {
            // Refresh failed (likely cookie not sent / expired). Fall back to logged-out state.
            logout();
          }
        }, refreshInMs);
      } catch {
        // ignore
      }
    };

    schedule();
    return () => {
      canceled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isAuthenticated]);

  const login = (userData, token) => {
    const accessToken = token?.accessToken ?? token?.AccessToken ?? token;
    const refreshToken = token?.refreshToken ?? token?.RefreshToken ?? null;

    if (accessToken) {
      setAccessToken(accessToken);
      if (refreshToken) setRefreshToken(refreshToken);
      setIsAuthenticated(true);
      // Set user data from token immediately
      const tokenData = getTokenUserData();
      if (tokenData) {
        setUser(tokenData);
      }
    }
    // Fetch full user data from database after login
    fetchCurrentUser();
  };

  const logout = async () => {
    try {
      // Call backend logout endpoint
      await userAPI.logout();
    } catch (error) {
      console.error('Backend logout failed:', error);
    }
    // Clear frontend state regardless of backend response
    setUser(null);
    setIsAuthenticated(false);
    clearTokens();
    setLoading(false);
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
